import React, { useState } from "react";
import PopoverDropdown from "../../../../elements/PopoverDropdown";
import { toast } from "react-toastify";
import { classNames } from "../../../../../helpers/classNames";
import { t } from "i18next";

function AddAttachment({ onChange = () => {}, uploadSize = 3 }) {
  const fileSelectHandler = (event) => {
    const accept = "jpg, jpeg, png,";
    const acceptList = accept?.split(",").map((elm) => elm.trim()); // Normalize input
    const files = Array.from(event.target.files || []); // Convert FileList to array

    if (!files.length) return;

    const validFiles = [];

    files.forEach((file) => {
      const fileExt = file.name.split(".").pop().toLowerCase(); // Extract file extension
      const fileType = file.type; // Get MIME type

      let isValid = true;

      if (acceptList && acceptList.length > 0) {
        isValid = acceptList.some((type) => {
          if (type.startsWith(".")) {
            return fileExt === type.replace(".", "").toLowerCase(); // Match extension
          }
          return fileExt === type || fileType === type; // Match MIME type
        });
      }

      if (!isValid) {
        toast.info(`Upload only ${acceptList?.join(", ")}`);
        return; // Skip this file
      }

      const fileSizeMB = file.size / (1024 * 1024);
      if (uploadSize && fileSizeMB > uploadSize) {
        toast.warning(`"${file.name}" must be less than ${uploadSize} MB`);
        return; // Skip this file
      }

      validFiles.push({
        name: file.name,
        url: URL.createObjectURL(file),
        file,
      });
    });

    if (validFiles.length) {
      onChange(validFiles);
    }

    event.target.value = ""; // Reset input
  };

  return (
    <PopoverDropdown
      position="top"
      contentClass={"min-w-40 p-1"}
      buttonLabel={<i className="fa-regular fa-plus"></i>}
    >
      <ul>
        <li className="rounded-md hover:bg-slate-100 transition-all duration-300">
          <label className="relative flex flex-col px-3 py-2 !cursor-pointer">
            <div className="flex gap-2 items-center justify-center text-sm font-medium">
              <i className="fa-regular fa-arrow-up-to-line"></i>
              <span className="text-xs">{t("Upload from this device")}</span>
            </div>
            <input
              type="file"
              className="hidden"
              multiple
              onChange={fileSelectHandler}
            />
          </label>
        </li>
      </ul>
    </PopoverDropdown>
  );
}

const ChatInput = ({ onSend = async () => {} }) => {
  const [loading, setLoading] = useState(false);
  const [uploadPercentage, setUploadPercentage] = useState(null);
  const [chatSubject, setChatSubject] = useState({
    text: "",
    attachment: [],
  });

  return (
    <div className="bg-white rounded-br-lg flex px-5 py-4 border intro-y chat-input box border-l-0 border-theme-3 dark:bg-dark-2 dark:border-dark-2 align-middle items-center">
      <AddAttachment
        onChange={(val) => {
          setChatSubject((pre) => ({ ...pre, attachment: val?.slice(0, 6) }));
          if (val?.length > 6) {
            toast.warning("maximum 6 file");
          }
        }}
      />
      <div className="flex-1 px-3 space-y-2 w-full">
        {Array.isArray(chatSubject?.attachment) &&
          chatSubject?.attachment?.length > 0 && (
            <div className="flex flex-wrap items-center justify-start gap-2">
              {chatSubject?.attachment?.map((file, i) => (
                <div
                  key={i?.toString()}
                  className="w-16 h-16 overflow-hidden border-slate-300 rounded-md shadow relative group"
                >
                  <img
                    src={file?.url}
                    alt={`${file?.name}${i}`}
                    className="w-full h-full object-cover object-center"
                  />
                  <div
                    className={classNames(
                      "absolute top-0 left-0 w-full h-full transition-all duration-500 bg-black/50 hidden items-center justify-center",
                      !uploadPercentage ? "group-hover:!flex" : "!flex"
                    )}
                  >
                    {!uploadPercentage ? (
                      <button
                        type="button"
                        onClick={() => {
                          setChatSubject((pre) => ({
                            ...pre,
                            attachment: pre?.attachment?.filter(
                              (_, idx) => idx !== i
                            ),
                          }));
                        }}
                      >
                        <i className="fa-regular fa-trash-can text-red-500"></i>
                      </button>
                    ) : (
                      <div className="relative text-white">
                        <i className="fa-regular fa-circle-notch text-4xl animate-spin"></i>
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[10px] font-medium">
                          {uploadPercentage}%
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        <textarea
          className="disabled:bg-slate-100 disabled:cursor-not-allowed dark:disabled:bg-darkmode-800/50 dark:disabled:border-transparent [&[readonly]]:bg-slate-100 [&[readonly]]:cursor-not-allowed [&[readonly]]:dark:bg-darkmode-800/50 [&[readonly]]:dark:border-transparent transition duration-200 ease-in-out w-full text-sm rounded-md placeholder:text-slate-400/90 focus:ring-4 focus:ring-primary focus:ring-opacity-20 focus:border-primary focus:border-opacity-40 dark:border-transparent dark:focus:ring-slate-700 dark:focus:ring-opacity-50 dark:placeholder:text-slate-500/80 h-12 px-5 py-3 mr-3 truncate border-transparent shadow-none resize-none focus:shadow-none sm:mr-0 dark:bg-darkmode-600 !border !border-gray-300"
          rows={1}
          placeholder={t("Type your message...")}
          value={chatSubject?.text}
          onChange={(e) => {
            setChatSubject((pre) => ({ ...pre, text: e.target.value }));
          }}
        />
      </div>

      <button
        disabled={loading}
        onClick={async () => {
          setLoading(true);
          const res = await onSend({
            value: {
              text: chatSubject?.text,
              attachments: (chatSubject?.attachment || [])?.map(
                (item) => item?.file
              ),
            },
            uploadPercentage: setUploadPercentage,
          });
          setUploadPercentage(null);
          setChatSubject({
            text: "",
            attachment: null,
          });
          setLoading(false);
        }}
        type="button"
        className="flex items-center justify-center flex-none w-8 h-8 text-white rounded-full bg-theme-1 sm:w-10 sm:h-10 bg-primary"
      >
        {loading ? (
          <i className="fa-regular fa-circle-notch animate-spin"></i>
        ) : (
          <i className="fa-light fa-paper-plane"></i>
        )}
      </button>
    </div>
  );
};

export default ChatInput;
